import { z } from 'zod';
import { EvidenceContentSchema, ReportFormatSchema, ReportTypeSchema } from './enums';
import {
  AssessmentIdSchema,
  EvidenceIdSchema,
  FindingIdSchema,
  PackBindingSchema,
  ReportIdSchema,
  TenantIdSchema,
  UserIdSchema,
} from './ids';
import { NonEmptyStringSchema, Sha256Schema, TimestampSchema } from './primitives';
import { LlmConfigSnapshotSchema } from './assessment';

/**
 * The evidence pack (DESIGN_SPEC §7.9, §15).
 *
 * An immutable bundle assembled at reporting time holding the full chain:
 * artefacts → extraction → canonical model → assumptions → clarification Q&A →
 * approved findings → mappings → recommendations → review log → reports, plus
 * model and pack versions, LLM config, and reviewer identity and date.
 *
 * `sha256` seals it. "Immutable" is a claim an auditor should be able to check,
 * not one they have to take on trust.
 */
export const EvidenceItemSchema = z
  .object({
    evidence_id: EvidenceIdSchema,
    tenant_id: TenantIdSchema,
    assessment_id: AssessmentIdSchema,
    contents: z.array(EvidenceContentSchema),
    storage_ref: NonEmptyStringSchema,
    sha256: Sha256Schema,

    /* Reproducibility anchors, copied in at assembly time (§12.2). */
    model_version: NonEmptyStringSchema,
    pack_versions: z.array(PackBindingSchema),
    llm_config: LlmConfigSnapshotSchema,

    reviewer_user_id: UserIdSchema,
    reviewed_at: TimestampSchema,
    assembled_at: TimestampSchema,
  })
  .superRefine((evidence, ctx) => {
    /*
     * **A pack showing approved findings must show the log of who approved
     * them** (docs/DECISIONS.md ZD-061 §5).
     *
     * The gate's honest claim is narrow: it contains false positives a competent
     * reader can recognise, and it establishes that a named person took
     * responsibility on a dated record. It does **not** verify grounding — code
     * does that before a human sees anything — and it does not reach a missing
     * finding at all. An evidence pack presenting `approved_findings` without
     * `review_log` and `reviewer_identity_and_date` therefore offers an auditor
     * the *conclusion* of a professional judgement with none of the working, in
     * the artefact whose whole purpose is the working (DESIGN_SPEC §15).
     *
     * A rule about a category list rather than about a document, so it costs
     * nothing until something assembles one — which nothing does. Declared here
     * rather than left for the assembling session to think of, because by then
     * a pack that omits it is an artefact somebody has already sent.
     */
    if (!evidence.contents.includes('approved_findings')) return;
    for (const required of ['review_log', 'reviewer_identity_and_date'] as const) {
      if (!evidence.contents.includes(required)) {
        ctx.addIssue({
          code: 'custom',
          message:
            `an evidence pack carrying approved_findings must also carry ${required}: an ` +
            'approval is a professional judgement, and the pack is where its working lives',
          path: ['contents'],
        });
      }
    }
  });
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * A generated report (DESIGN_SPEC §7.9, §14.2).
 *
 * Every id in `source_finding_ids` must be a `client_ready` finding. That is
 * enforced by the guard on the `report_generated` transition rather than here,
 * because finding state lives outside this object — but it is the reason the
 * report records which findings it was built from at all.
 */
export const ReportSchema = z.object({
  report_id: ReportIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  type: ReportTypeSchema,
  format: ReportFormatSchema,
  storage_ref: NonEmptyStringSchema,
  sha256: Sha256Schema,
  source_finding_ids: z.array(FindingIdSchema),
  generated_at: TimestampSchema,
});
export type Report = z.infer<typeof ReportSchema>;
